import { useEffect, useState } from 'react';
import { BarChart, LineChart } from '@mantine/charts';
import PropTypes from 'prop-types';
import { withErrorBoundary } from 'react-error-boundary';
import {
  Container,
  Group,
  LoadingOverlay,
  Select,
  Text,
} from '@mantine/core';
import { CHART_TYPES, MONTHS } from '@/helpers/enums';
import prepareTranslate from '@/helpers/dictionary';
import { isEqual } from 'lodash';
import { createYearsArray } from '@/helpers/dates';
import ErrorFallback from '../ErrorFallback';
import generateMockChartData from './mockData/mockChartData';
import mockSeries from './mockData/mockSeries';
import {
  attachColorsToSeries,
  formatData,
  prepareToolTipData,
  validateData,
} from './LineChart.functions';
import NoData from './noData';
import ChartType from './ChartType';
import styles from './style.module.css';
import CustomTick from './CustomTick/CustomTick';

const chartOptions = ['Bar', 'Line'];

const ChartComponent = (props) => {
  const {
    getData = () => { },
    getDataQuery,
    dataKey = 'category',
    h = '16rem',
    curveType = 'linear',
    isChartTypeActive = true,
    useDateSelectors = false,
    onBarClick,
    defaultValue,
    defaultFilters,
    noDataAction,
    seriesObj = [],
    chartCategory,
    showTotalSum,
  } = props;

  const mockChartData = generateMockChartData();
  const t = prepareTranslate();
  const [data, setData] = useState(mockChartData); // data from back end
  const [formattedData, setFormattedData] = useState([]); // data without components to match chart
  const [series, setSeries] = useState(seriesObj);
  const [primaryValue, setPrimaryValue] = useState();
  const [loading, setLoading] = useState(true);
  const [chartType, setChartType] = useState(chartOptions);
  const [filters, setfilters] = useState(defaultFilters);
  const [sum, setSum] = useState(0);
  const [hoveredCategory, setHoveredCategory] = useState(null);

  const onChartTypeSelect = (value) => {
    setChartType(value);
  };

  const formatNumberWithSeparator = (num) => num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');

  const getUpdatedQuery = () => {
    const updatedQuery = { ...getDataQuery };
    if (useDateSelectors) {
      updatedQuery.month = [filters.month];
      updatedQuery.year = [filters.year];
    }
    return updatedQuery;
  };

  /**
   * Call server with missing data, and fill "data", "formattedData" and "series"  state
   */
  const fillDataFromServer = async (value) => {
    setLoading(true);
    const isdefaultvalue = isEqual(defaultFilters, filters) && value;
    const newRows = isdefaultvalue ? value : await getData(getUpdatedQuery());
    setLoading(false);

    const {
      data: newData,
      series: newSeries,
      primaryValue: newPrimaryValue,
    } = newRows;

    if (!validateData(newData, dataKey, series, newPrimaryValue)) {
      // eslint-disable-next-line no-console
      console.error('getData is not valid');
      setData([]);
      setSum(null);
      return;
    }

    const coloredSeries = attachColorsToSeries(newSeries);
    const newformattedData = formatData(newData);

    // Removes zero-value fields and filters out items with all zero values.
    const filteredData = newformattedData.map((item) => {
      const newItem = { ...item };
      let allZero = true;
      newSeries.forEach(({ name }) => {
        if (newItem[name] === 0) {
          delete newItem[name];
        } else {
          allZero = false;
        }
      });
      return allZero ? null : newItem;
    }).filter(Boolean);

    setFormattedData(filteredData);
    setData(newData);
    setSeries(coloredSeries);
    setPrimaryValue(newPrimaryValue);
    const total = filteredData.reduce((number, item) => {
      Object.entries(item).forEach(([key, val]) => {
        if (typeof val === 'number' && key !== 'year' && key !== 'month' && key !== 'category') {
          // eslint-disable-next-line no-param-reassign
          number += val;
        }
      });
      return number;
    }, 0);

    setSum(formatNumberWithSeparator(Math.floor(total)));
  };

  useEffect(() => {
    fillDataFromServer(defaultValue);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getDataQuery, filters]);

  const chartData = loading ? mockChartData : formattedData;
  const currentSeries = loading ? mockSeries : series;
  const emptyData = (!loading && data.length === 0);
  const showChart = chartCategory !== 'product' || (chartCategory === 'product' && chartData.length !== 1);

  const valueFormatter = (value) => {
    const absValue = Math.abs(value);
    const isNegative = value < 0;
    const sign = isNegative ? '-' : '';

    let formatted;
    if (absValue >= 1_000_000_000) {
      formatted = `${(absValue / 1_000_000_000).toFixed(1)}B`;
    } else if (absValue >= 1_000_000) {
      formatted = `${(absValue / 1_000_000).toFixed(1)}M`; // Millions
    } else if (absValue >= 1_000) {
      formatted = `${(absValue / 1_000).toFixed(1)}K`; // Thousands
    } else {
      formatted = new Intl.NumberFormat('en-US').format(Math.floor(absValue));
    }

    return `${sign}${formatted}`;
  };

  const values = chartData.map((item) => currentSeries.reduce(
    (sum1, s) => sum1 + (item[s.name] ?? 0),
    0,
  ));

  // Handle both positive and negative values
  const nonZeroValues = values.filter((v) => v !== 0);
  const hasNegativeValues = nonZeroValues.some((v) => v < 0);

  let min;
  let max;

  if (nonZeroValues.length > 0) {
    min = Math.min(...nonZeroValues);
    max = Math.max(...nonZeroValues);

    // If we have negative values, ensure the domain starts from below zero
    if (hasNegativeValues && min >= 0) {
      min = Math.min(...nonZeroValues.filter((v) => v < 0));
    }

    // Add some padding to make the chart look better
    const padding = (max - min) * 0.1;
    min = hasNegativeValues ? min - padding : Math.max(0, min - padding);
    max += padding;
  } else {
    // Default values when no data
    min = 0;
    max = 100;
  }

  const lineChartData = chartData.map((item) => ({
    ...item,
    [primaryValue]: currentSeries.reduce((sumLine, s) => sumLine + (item[s.name] ?? 0), 0),
  }));

  const handleMouseEnter = (bar) => {
    const hoveredItem = chartData.find(
      (chartItem) => chartItem[dataKey] === bar.payload[dataKey],
    );
    setHoveredCategory(hoveredItem ? hoveredItem[dataKey] : null);
  };

  return (
    showChart ? (
      <Container fluid p={'1rem'} m={0} pos='relative' bg={showChart ? 'white' : 'none'}>
        <LoadingOverlay
          visible={loading}
        />
        {!emptyData && (
          <Group>
            {isChartTypeActive && (
              <Container mr={0} p={'1.5rem'} w="10rem">
                <ChartType
                  onChartTypeSelect={onChartTypeSelect}
                />
              </Container>
            )}
          </Group>
        )}
        {useDateSelectors && (
          <>
            <div className={styles.selectDateContainer}>
              <Select
                w="7rem"
                data={MONTHS}
                value={filters.month}
                onChange={(value) => setfilters({ ...filters, month: value })}
              />
              <Select
                w="7rem"
                autoComplete="off"
                data={createYearsArray()}
                value={filters.year}
                onChange={(value) => setfilters({ ...filters, year: value })}
              />
            </div>
            {sum && <Text ta='center' mt='s' mb='xl' pt={'1rem'}>{t('components.chart.sum', [sum])}</Text>
            }
          </>
        )}
          {
          // eslint-disable-next-line no-nested-ternary
          emptyData ? (
            <NoData noDataAction={noDataAction} title={t('components.chart.noRows.title')} />
          ) : (
            chartType === CHART_TYPES.line ? (
              <LineChart
                h={h}
                data={lineChartData}
                series={[{ name: primaryValue }]}
                dataKey={dataKey}
                curveType={curveType}
                withXAxis={!loading}
                withYAxis={!loading}
                withTooltip={!loading}
                tooltipProps={{
                  content: ({ label, payload }) => (
                    prepareToolTipData(data, label, payload, primaryValue)
                  ),
                  wrapperStyle: { zIndex: 1000000 },
                }}
                valueFormatter={valueFormatter}
                yAxisProps={{
                  // Use log scale only for positive values, linear for negative or mixed
                  scale: hasNegativeValues ? 'linear' : 'log',
                  domain: hasNegativeValues
                    ? [min, max]
                    : [Math.max(1, min), Math.max(10, max)],
                }}
                referenceLines={hasNegativeValues ? [
                  {
                    y: 0,
                    color: 'red.6',
                    strokeWidth: 2,
                    label: '0',
                    labelPosition: 'insideTopLeft',
                  },
                ] : []}
              />
            ) : (
              <BarChart
                h={h}
                type="stacked"
                data={chartData}
                dataKey={dataKey}
                series={currentSeries}
                withLegend={!loading && !useDateSelectors}
                tooltipProps={{
                  content: ({ label, payload }) => (
                    prepareToolTipData(data, label, payload, primaryValue)
                  ),
                  wrapperStyle: { zIndex: 1000000 },
                }}
                barProps={(item) => ({
                  ...item,
                  onClick: (bar) => {
                    onBarClick(getUpdatedQuery(), bar);
                  },
                  onMouseEnter: handleMouseEnter,
                  onMouseLeave: () => {
                    setHoveredCategory(null);
                  },
                  className: onBarClick ? styles.bar : '',
                })}
                valueFormatter={valueFormatter}
                xAxisProps={{
                  tick: (tickProps) => (
                    <CustomTick
                      {...tickProps}
                      chartData={chartData}
                      dataKey={dataKey}
                      hoveredCategory={hoveredCategory}
                    />
                  ),
                  height: 40,
                  interval: 0,
                }}
                yAxisProps={{
                  domain: [min, max],
                }}
                referenceLines={hasNegativeValues ? [
                  {
                    y: 0,
                    color: 'red.6',
                    strokeWidth: 2,
                    labelPosition: 'insideTopLeft',
                  },
                ] : []}
              />
            )
          )
        }
        <div className={styles.sumArea}>
          {showTotalSum && <Text className={styles.chartTotalSum}>{t('components.chart.totalSum', [sum])}</Text>}
        </div>
      </Container>
    ) : null
  );
};

ChartComponent.propTypes = {
  getData: PropTypes.func,
  getDataQuery: PropTypes.object,
  dataKey: PropTypes.string,
  h: PropTypes.number,
  curveType: PropTypes.string,
  isChartTypeActive: PropTypes.bool,
  useDateSelectors: PropTypes.bool,
  defaultMonth: PropTypes.string,
  defaultYear: PropTypes.string,
  onBarClick: PropTypes.func,
  defaultValue: PropTypes.object,
  defaultFilters: PropTypes.object,
  noDataAction: PropTypes.string,
  seriesObj: PropTypes.array,
  chartCategory: PropTypes.string,
  showTotalSum: PropTypes.bool,
};
export default withErrorBoundary(ChartComponent, {
  FallbackComponent: ErrorFallback,
});
